import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { FlexBox, LabeledValue, Tooltip } from 'core/elements';
import { formatDate } from 'core/utils';
import { useCostDecimals } from 'pricing/hooks';
import { IPricingSourceData } from 'quotes/types';
import {
  getPricingSourceDisplayFields,
  isSelectedSpaSource,
  isSpaSourceInUse,
  isTradeAgreementExpired,
} from 'quotes/utils';
import { Color, Level, Size, Variant } from 'theme/enums';

import {
  pricingSourceNotInUseSx,
  pricingSourceNotInUseValueSx,
} from './ExternalPricingSourceContent';

interface IProps {
  // From getEffectiveSpaInfo.
  pricingData: IPricingSourceData;
  spaCostCurrency?: string | null;
  selectedSpaNotOfferedMessage?: string | null;
  // A prop, so this element never imports the picker (a barrel cycle).
  action?: ReactNode;
  // Mounts the card for its action alone, on a line with neither an automatic SPA nor a pick.
  isActionOnlyVisible?: boolean;
  isAlternateVersionEnabled?: boolean;
  isShipAndDebitVendor?: boolean;
}

const gridMdSizeMap: Record<string, number> = {
  labelExternalSpaCostSourceShort: 4,
  labelExternalSpaCostSourceNameShort: 8,
  labelExternalSpaCostRegistrationNumShort: 4,
  labelExternalSpaCostEndDateShort: 4,
  labelExternalSpaCostShort: 4,
};

const DEFAULT_MD_SIZE = 4;

export const ExternalPricingSourceCard = ({
  pricingData,
  spaCostCurrency,
  selectedSpaNotOfferedMessage,
  action,
  isActionOnlyVisible = false,
  isAlternateVersionEnabled,
  isShipAndDebitVendor,
}: IProps) => {
  const { t } = useTranslation();
  const costDecimals = useCostDecimals();

  const displayFields = getPricingSourceDisplayFields(pricingData, costDecimals, {
    isShortLabel: true,
    spaCostCurrency,
  });
  const hasSpaData = Object.entries(pricingData ?? {}).some(
    ([key, value]) => 'sourceType' !== key && value != null && value !== ''
  );

  // Ship-and-debit lines keep the section mounted whether or not the pricing engine returned SPA
  // information, so it stops appearing and disappearing between recalculations.
  if (
    !isAlternateVersionEnabled ||
    (!hasSpaData && !isShipAndDebitVendor && !isActionOnlyVisible)
  ) {
    return null;
  }

  // Any other source strikes the engine's agreement.
  const isSpaInUse = hasSpaData && isSpaSourceInUse(pricingData.sourceType);
  const isStruckThrough = hasSpaData && !isSpaInUse;
  // Same fields as the automatic block, so only this says the agreement is the chosen one.
  const isSelectedSpaShown = hasSpaData && isSelectedSpaSource(pricingData.sourceType);
  // Expired as the drawer greys its rows out.
  const selectedSpaEndDate = pricingData.externalSpaCostEndDate as string | undefined;
  const isSelectedSpaExpired =
    isSelectedSpaShown && isTradeAgreementExpired({ spaCostEndDate: selectedSpaEndDate });
  const selectedSpaExpiredMessage =
    isSelectedSpaExpired &&
    t('messageSelectedSpaExpired', { date: formatDate(selectedSpaEndDate) });
  const selectedSpaWarnings = isSelectedSpaShown
    ? [selectedSpaExpiredMessage, selectedSpaNotOfferedMessage].filter(
        (warning): warning is string => !!warning
      )
    : [];

  return (
    <Card
      variant={Variant.SOFT}
      color={isSpaInUse ? Color.WARNING : Color.NEUTRAL}
      sx={{ width: '100%', height: '100%' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography level={Level.TITLE_SM} sx={isSpaInUse ? {} : { color: 'text.tertiary' }}>
          {t('labelSpaCostDetails')}
        </Typography>

        {isSelectedSpaShown && (
          <Chip color={Color.SUCCESS} variant={Variant.SOFT} size={Size.SM}>
            {t('labelSelected')}
          </Chip>
        )}

        {!!selectedSpaWarnings.length && (
          <Tooltip
            title={
              <Stack>
                {selectedSpaWarnings.map((warning) => (
                  <span key={warning}>{warning}</span>
                ))}
              </Stack>
            }
            color={Color.DANGER}
          >
            <FlexBox
              role="img"
              aria-label={selectedSpaWarnings.join(' ')}
              sx={{ color: 'danger.softColor', fontSize: 16 }}
            >
              <Warning />
            </FlexBox>
          </Tooltip>
        )}

        {!!action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
      </Box>

      <CardContent sx={{ gap: 'inherit', ...(isStruckThrough && pricingSourceNotInUseSx) }}>
        {!hasSpaData ? (
          <Typography level={Level.BODY_XS} sx={{ color: 'text.tertiary' }}>
            {t('messageNoSpaInformation')}
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {displayFields.map(({ labelKey, value }) => (
              <Grid xs={12} md={gridMdSizeMap[labelKey] || DEFAULT_MD_SIZE} key={labelKey}>
                <LabeledValue
                  label={t(labelKey)}
                  value={value || DEFAULT_PLACEHOLDER}
                  options={{
                    labelLevel: Level.BODY_XS,
                    labelSx: { color: 'inherit', whiteSpace: 'nowrap' },
                    hasColon: false,
                    valueSx: {
                      color: 'inherit',
                      fontWeight: 600,
                      ...(isStruckThrough && pricingSourceNotInUseValueSx),
                    },
                    valueEllipsis: true,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
